import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useMemo } from 'react';

import {
  DirectVisitorFollowupMessageFragment,
  DirectVisitorMessagesFragment,
  GetDirectMessagesOnOutpatientQuestionnaireEntryKarteDocument,
  GetLineMessagesOnOutpatientQuestionnaireEntryKarteDocument,
  LineVisitorFollowupMessageFragment,
  LineVisitorMessagesFragment,
  ScheduledDirectVisitorMessageFragment,
  ScheduledDirectVisitorMessagesFragment,
  ScheduledLineVisitorMessageFragment,
  ScheduledLineVisitorMessagesFragment,
} from '~/graphql/generated/graphql';

type VisitorType = 'DirectVisitor' | 'LineVisitor';
type Message = DirectVisitorFollowupMessageFragment | LineVisitorFollowupMessageFragment;
type ScheduledMessage = ScheduledDirectVisitorMessageFragment | ScheduledLineVisitorMessageFragment;

export const useFetchMessages = (patientId: string, visitorType: VisitorType) => {
  const [getDirectMessages, { data: directData, loading: directLoading }] = useLazyQuery(
    GetDirectMessagesOnOutpatientQuestionnaireEntryKarteDocument,
  );
  const [getLineMessages, { data: lineData, loading: lineLoading }] = useLazyQuery(
    GetLineMessagesOnOutpatientQuestionnaireEntryKarteDocument,
  );

  useEffect(() => {
    if (patientId && visitorType === 'DirectVisitor') {
      getDirectMessages({ variables: { id: patientId } });
    } else if (patientId && visitorType === 'LineVisitor') {
      getLineMessages({ variables: { id: patientId } });
    }
  }, [patientId, visitorType, getDirectMessages, getLineMessages]);

  const { messages, scheduledMessages } = useMemo(() => {
    const data = visitorType === 'DirectVisitor' ? directData : lineData;

    const allMessages =
      (data?.all as DirectVisitorMessagesFragment | LineVisitorMessagesFragment)
        ?.medicationFollowupMessages.nodes || [];

    const scheduled =
      (
        data?.scheduled as
          | ScheduledDirectVisitorMessagesFragment
          | ScheduledLineVisitorMessagesFragment
      )?.medicationFollowupMessages.nodes || [];

    const parsedMessages = allMessages.map((node) => node as Message);
    const parsedScheduledMessages = scheduled.map((node) => node as ScheduledMessage);

    return {
      messages: parsedMessages,
      scheduledMessages: parsedScheduledMessages,
    };
  }, [directData, lineData, visitorType]);

  return {
    messages,
    scheduledMessages,
    loading: directLoading || lineLoading,
  };
};
